import apiRequest from './apiClient';

// ---------------------------------------------------------------------------
// Org settings
// ---------------------------------------------------------------------------

/**
 * Получить настройки организации (tournamentsEnabled, sport).
 * GET /api/organizations/me/settings
 */
export const getOrgSettings = () =>
  apiRequest('/api/organizations/me/settings');

/**
 * Обновить настройки организации. Только владелец.
 * PATCH /api/organizations/me/settings
 * @param {{ tournamentsEnabled?: boolean, sport?: string }} data
 */
export const updateOrgSettings = (data) =>
  apiRequest('/api/organizations/me/settings', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

// ---------------------------------------------------------------------------
// Турнир — CRUD
// ---------------------------------------------------------------------------

/**
 * Создать турнир для события.
 * POST /api/events/{publicEventId}/tournaments
 * @param {string} publicEventId
 * @param {{ format, sport, courtsCount, matchScoring, matchDurationMinutes?, rankingMode?, randomSeeding? }} data
 *        rankingMode: 'POINTS' (по сумме очков) | 'FOOTBALL' (по победам, 1/0/0). По умолчанию POINTS.
 *        randomSeeding: true — случайный порядок (дефолт), false — ручной (по seed).
 *        Применимо к Mexicano / Beat the Box; для Americano игнорируется (бэк ставит null).
 */
export const createTournament = (publicEventId, data) =>
  apiRequest(`/api/events/${publicEventId}/tournaments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

/**
 * Список турниров события (лёгкий DTO, без players/rounds, с playersCount).
 * Отсортировано по createdAt ASC (старые сначала).
 * GET /api/events/{publicEventId}/tournaments
 * @returns {Promise<Array<{ publicId, status, format, sport, courtsCount, playersCount, ... }>>}
 */
export const getEventTournaments = (publicEventId) =>
  apiRequest(`/api/events/${publicEventId}/tournaments`);

/**
 * Получить турнир по publicId.
 * GET /api/tournaments/{publicId}
 */
export const getTournament = (publicId) =>
  apiRequest(`/api/tournaments/${publicId}`);

/**
 * Обновить настройки турнира. Большинство полей — только в DRAFT/RUNNING;
 * rankingMode можно менять в любом статусе (это лишь способ сортировки).
 * PATCH /api/tournaments/{publicId}
 * @param {string} publicId
 * @param {{ courtsCount?, format?, sport?, matchScoring?, matchDurationMinutes?, rankingMode?, randomSeeding? }} data
 *        randomSeeding: true — случайный порядок, false — ручной (по seed). Меняется
 *        в DRAFT и в RUNNING до первого сыгранного матча.
 * @param {string} [clientId] — X-Client-Id для подавления собственного SSE-алерта
 */
export const updateTournament = (publicId, data, clientId) =>
  apiRequest(`/api/tournaments/${publicId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

/**
 * Задать ручной порядок (посев) игроков турнира.
 * PATCH /api/tournaments/{publicId}/players/order
 * Проставляет seed по порядку organizationMemberIds и включает ручной режим
 * (randomSeeding=false). Доступно для Mexicano / Beat the Box, пока ни один матч
 * не сыгран.
 * @param {string} publicId
 * @param {number[]} organizationMemberIds — id участников организации в желаемом порядке
 *        (стабильны, существуют до создания TournamentPlayer)
 * @param {string} [clientId] — X-Client-Id для подавления собственного SSE-алерта
 */
export const updatePlayerOrder = (publicId, organizationMemberIds, clientId) =>
  apiRequest(`/api/tournaments/${publicId}/players/order`, {
    method: 'PATCH',
    body: JSON.stringify({ organizationMemberIds }),
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

// ---------------------------------------------------------------------------
// Состав игроков
// ---------------------------------------------------------------------------

/**
 * Добавить игрока в турнир из CONFIRMED-участников события.
 * POST /api/tournaments/{publicId}/players
 * @param {string} publicId
 * @param {{ organizationMemberId: number }} data
 */
export const addPlayer = (publicId, data) =>
  apiRequest(`/api/tournaments/${publicId}/players`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

/**
 * Изменить статус игрока (ACTIVE / REMOVED).
 * PATCH /api/tournaments/{publicId}/players/{playerId}
 * @param {string} publicId
 * @param {number} playerId
 * @param {{ status: 'ACTIVE' | 'REMOVED' }} data
 */
export const updatePlayerStatus = (publicId, playerId, data) =>
  apiRequest(`/api/tournaments/${publicId}/players/${playerId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });

/**
 * Удалить игрока из турнира (только в DRAFT — физическое удаление).
 * DELETE /api/tournaments/{publicId}/players/{playerId}
 */
export const deletePlayer = (publicId, playerId) =>
  apiRequest(`/api/tournaments/${publicId}/players/${playerId}`, {
    method: 'DELETE',
  });

/**
 * CONFIRMED-участники события, которых ещё нет в турнире — для UI «Добавить».
 * GET /api/tournaments/{publicId}/available-members
 * @returns {Promise<Array<{ organizationMemberId, displayName, alreadyInTournament }>>}
 */
export const getAvailableMembers = (publicId) =>
  apiRequest(`/api/tournaments/${publicId}/available-members`);

/**
 * CONFIRMED-участники события до создания турнира — для выбора состава на форме создания.
 * GET /api/events/{publicEventId}/confirmed-members
 * @returns {Promise<Array<{ organizationMemberId, displayName }>>}
 */
export const getEventConfirmedMembers = (publicEventId) =>
  apiRequest(`/api/events/${publicEventId}/confirmed-members`);

/**
 * Таблица лидеров турнира — игроки с полной статистикой, отсортированные по rankingMode.
 * GET /api/tournaments/{publicId}/leaderboard[?stage=1|2]
 * @param {string} publicId
 * @param {number} [stage] — только для Beat the Box: 1|2 вернёт таблицу конкретного
 *        этапа (нужно для свёрнутой истории этапа 1 во время этапа 2). Без параметра —
 *        активная таблица (этап 2, если он идёт, иначе этап 1).
 * @returns {Promise<Array<TournamentPlayerResponse>>}
 */
export const getLeaderboard = (publicId, stage) =>
  apiRequest(`/api/tournaments/${publicId}/leaderboard${stage != null ? `?stage=${stage}` : ''}`);

// ---------------------------------------------------------------------------
// Жизненный цикл
// ---------------------------------------------------------------------------

/**
 * Запустить турнир (DRAFT → RUNNING), генерирует расписание.
 * POST /api/tournaments/{publicId}/start
 */
export const startTournament = (publicId) =>
  apiRequest(`/api/tournaments/${publicId}/start`, {
    method: 'POST',
  });

/**
 * Перегенерировать незавершённые туры после правки состава в RUNNING.
 * POST /api/tournaments/{publicId}/regenerate-upcoming
 */
export const regenerateUpcoming = (publicId, clientId) =>
  apiRequest(`/api/tournaments/${publicId}/regenerate-upcoming`, {
    method: 'POST',
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

/**
 * Завершить турнир (RUNNING → FINISHED).
 * POST /api/tournaments/{publicId}/finish
 * @param {string} [clientId] — X-Client-Id для подавления собственного SSE-алерта
 */
export const finishTournament = (publicId, clientId) =>
  apiRequest(`/api/tournaments/${publicId}/finish`, {
    method: 'POST',
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

/**
 * Вернуть турнир в работу (FINISHED → RUNNING).
 * POST /api/tournaments/{publicId}/reopen
 * @param {string} [clientId] — X-Client-Id для подавления собственного SSE-алерта
 */
export const reopenTournament = (publicId, clientId) =>
  apiRequest(`/api/tournaments/${publicId}/reopen`, {
    method: 'POST',
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

/**
 * Отменить турнир (* → CANCELLED).
 * POST /api/tournaments/{publicId}/cancel
 * TODO: эндпоинт ещё не добавлен в TournamentController на бэке
 */
export const cancelTournament = (publicId) =>
  apiRequest(`/api/tournaments/${publicId}/cancel`, {
    method: 'POST',
  });

/**
 * Мягкое удаление турнира (→ DELETED). FINISHED удалить нельзя.
 * DELETE /api/tournaments/{publicId}
 */
export const deleteTournament = (publicId) =>
  apiRequest(`/api/tournaments/${publicId}`, {
    method: 'DELETE',
  });

// ---------------------------------------------------------------------------
// Таймер (режим TIME)
// ---------------------------------------------------------------------------

/**
 * Запустить таймер обратного отсчёта для текущего раунда.
 * POST /api/tournaments/{publicId}/timer/start
 * Записывает текущий момент как timerStartedAt; фронт считает остаток сам.
 * @param {string} [clientId] — X-Client-Id для подавления собственного SSE-алерта
 */
export const startTimer = (publicId, clientId) =>
  apiRequest(`/api/tournaments/${publicId}/timer/start`, {
    method: 'POST',
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

/**
 * Сбросить таймер (timerStartedAt → null).
 * POST /api/tournaments/{publicId}/timer/reset
 * @param {string} [clientId] — X-Client-Id для подавления собственного SSE-алерта
 */
export const resetTimer = (publicId, clientId) =>
  apiRequest(`/api/tournaments/${publicId}/timer/reset`, {
    method: 'POST',
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

// ---------------------------------------------------------------------------
// Матчи
// ---------------------------------------------------------------------------

/**
 * Сохранить счёт матча.
 * PATCH /api/tournaments/{publicId}/matches/{matchId}/score
 * @param {string} publicId
 * @param {number} matchId
 * @param {{ teamAScore: number, teamBScore: number, version: number }} data
 *   version — текущая версия матча для optimistic locking (из match.version)
 * @param {string} [clientId] — X-Client-Id для подавления собственного SSE-алерта
 */
export const submitMatchScore = (publicId, matchId, data, clientId) =>
  apiRequest(`/api/tournaments/${publicId}/matches/${matchId}/score`, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

/**
 * Обновить статус матча (PENDING / IN_PROGRESS / FINISHED).
 * PATCH /api/tournaments/{publicId}/matches/{matchId}/status
 * @param {string} publicId
 * @param {number} matchId
 * @param {{ status: string, version: number }} data
 *   version — текущая версия матча для optimistic locking (из match.version)
 * @param {string} [clientId] — X-Client-Id для подавления собственного SSE-алерта
 */
export const updateMatchStatus = (publicId, matchId, data, clientId) =>
  apiRequest(`/api/tournaments/${publicId}/matches/${matchId}/status`, {
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: clientId ? { 'X-Client-Id': clientId } : undefined,
  });

// ---------------------------------------------------------------------------
// SSE-стрим для организаторов (приватная страница)
// ---------------------------------------------------------------------------

const API_BASE_URL_SSE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const getInitData = () => {
  if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData) {
    return window.Telegram.WebApp.initData;
  }
  return process.env.REACT_APP_DEV_INIT_DATA || 'non-telegram-mode';
};

/**
 * SSE-подписка для приватной страницы турнира (с аутентификацией).
 * initData передаётся как query-параметр (EventSource не поддерживает заголовки).
 *
 * onUpdate(payload) — payload: { tournament: TournamentResponse, leaderboard: [...], initiatorClientId }
 * onError()         — вызывается при обрыве соединения
 *
 * @param {string} [clientId] — X-Client-Id этой вкладки; бэк помечает им начальный
 *        снапшот (initiatorClientId), чтобы вкладка узнала свою базовую синхронизацию
 *        и не показала ложный алерт при переподключении. EventSource не умеет
 *        заголовки — передаём query-параметром (как initData).
 * @returns {EventSource}
 */
export const subscribePrivateTournamentStream = (publicId, onUpdate, onError, clientId) => {
  const initData = encodeURIComponent(getInitData());
  let url = `${API_BASE_URL_SSE}/api/tournaments/${publicId}/stream?initData=${initData}`;
  if (clientId) url += `&clientId=${encodeURIComponent(clientId)}`;
  const es = new EventSource(url);

  es.addEventListener('update', (e) => {
    try {
      onUpdate(JSON.parse(e.data));
    } catch (_) {
      // ignore malformed event
    }
  });

  if (onError) {
    es.onerror = onError;
  }

  return es;
};
